import {
  ProductTestKit,
  ProductFormulas,
  FormulaIncludes,
  ContentfulIncludes,
  ContentfulImage,
  ContentfulAsset,
  ContentfulEntry,
  ContentfulProductTestKitEntry,
  ContentfulProductFormulaEntry,
} from './ContentfulTypes';

export const getImageUrlFromIncludes = (
  imageId: string,
  includes: ContentfulIncludes,
): string | undefined => {
  console.log(`🔍 Looking up image URL for asset ID: ${imageId}`);

  if (!includes || !includes.Asset) {
    console.warn('⚠️ No includes.Asset found in response');
    return undefined;
  }

  const asset = includes.Asset.find(
    (asset: ContentfulAsset) => asset.sys.id === imageId,
  );

  if (!asset) {
    console.warn(`⚠️ Could not find asset with ID: ${imageId} in includes`);
    return undefined;
  }

  if (asset && asset.fields.file && asset.fields.file.url) {
    // URLs typically start with // so add https: prefix
    const imageUrl = asset.fields.file.url;
    const finalUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
    console.log(`✅ Found image URL: ${finalUrl}`);
    return finalUrl;
  }

  console.warn(`⚠️ Asset found but no valid URL for ID: ${imageId}`);
  return undefined;
};

export const mapToProductTestKit = (
  item: ContentfulProductTestKitEntry,
  includes: ContentfulIncludes,
): ProductTestKit => {
  console.log(`🔄 Mapping entry to ProductTestKit: ${item.sys.id}`);

  const productTestKit: ProductTestKit = {
    testId: item.fields.testId,
    title: String(item.fields.title || ''),
    description: item.fields.description
      ? String(item.fields.description)
      : undefined,
    productMessage: item.fields.productMessage
      ? String(item.fields.productMessage)
      : undefined,
    promo: item.fields.promo,
    cta: item.fields.cta,
    couponCode: item.fields.couponCode
      ? String(item.fields.couponCode)
      : undefined,
    discountPercentage: item.fields.discountPercentage
      ? Number(item.fields.discountPercentage)
      : undefined,
    discountAmount: item.fields.discountAmount
      ? Number(item.fields.discountAmount)
      : undefined,
  };

  console.log(`📦 Basic fields mapped for: "${productTestKit.title}"`);

  try {
    if (
      item.fields.productImage &&
      typeof item.fields.productImage === 'object'
    ) {
      const imageData = item.fields.productImage as ContentfulImage;
      if (imageData.sys && imageData.sys.id) {
        const imageId = imageData.sys.id;
        // Look up and store the image URL from includes
        if (includes) {
          productTestKit.productImageUrl = getImageUrlFromIncludes(
            imageId,
            includes,
          );
        } else {
          console.warn('⚠️ No includes object provided for image URL lookup');
        }
      }
    } else {
      console.log('ℹ️ No productImage field found or invalid format');
    }
  } catch (e) {
    console.warn('⚠️ Could not process productImage field:', e);
  }

  return productTestKit;
};

export const getLinkedEntryFromIncludes = (
  entryId: string,
  includes: ContentfulIncludes,
): ContentfulEntry | undefined => {
  if (!includes || !includes.Entry) {
    console.warn('⚠️ No includes.Entry found in response');
    return undefined;
  }

  const entry = includes.Entry.find(
    (entry: ContentfulEntry) => entry.sys.id === entryId,
  );

  if (!entry) {
    console.warn(`⚠️ Could not find linked entry with ID: ${entryId}`);
    return undefined;
  }
  return entry;
};

export const mapToProductFormula = (
  item: ContentfulProductFormulaEntry,
  includes: ContentfulIncludes,
): ProductFormulas => {
  const productFormula: ProductFormulas = {
    title: String(item.fields.title || ''),
    productId: String(item.fields.productId || ''),
    shortDescription: item.fields.shortDescription
      ? String(item.fields.shortDescription)
      : undefined,
    longDescription: item.fields.longDescription
      ? String(item.fields.longDescription)
      : undefined,
    promoVideoUrl: item.fields.promoVideoUrl
      ? String(item.fields.promoVideoUrl)
      : undefined,
    formulaShortDescription: item.fields.formulaShortDescription
      ? String(item.fields.formulaShortDescription)
      : undefined,
    formulaProfile: item.fields.formulaProfile || undefined,
    disclaimer: item.fields.disclaimer
      ? String(item.fields.disclaimer)
      : undefined,
    formulaBenefits: Array.isArray(item.fields.formulaBenefits)
      ? item.fields.formulaBenefits.map((benefit: string) => String(benefit))
      : undefined,
    productReferences: Array.isArray(item.fields.productReferences)
      ? item.fields.productReferences.map((reference: string) =>
          String(reference),
        )
      : undefined,
    color: item.fields.color ? String(item.fields.color) : undefined,
  };

  // Handle product image
  try {
    if (
      item.fields.productImage &&
      typeof item.fields.productImage === 'object'
    ) {
      const imageData = item.fields.productImage;
      if (imageData.sys && imageData.sys.id) {
        const imageId = imageData.sys.id;
        if (includes) {
          productFormula.productImageUrl = getImageUrlFromIncludes(
            imageId,
            includes,
          );
        } else {
          console.warn('⚠️ No includes object provided for image URL lookup');
        }
      }
    } else {
      console.log('ℹ️ No productImage field found or invalid format');
    }
  } catch (e) {
    console.warn('⚠️ Could not process productImage field:', e);
  }

  // Handle formula includes
  try {
    if (Array.isArray(item.fields.formulaIncludes)) {
      const mappedIncludes = item.fields.formulaIncludes.map((include) => {
        const includeItem: FormulaIncludes = {
          title: include.title ? String(include.title) : undefined,
          imageUrl: include.imageUrl ? String(include.imageUrl) : undefined,
        };
        return includeItem;
      });

      productFormula.formulaIncludes = mappedIncludes;
    }
  } catch (e) {
    console.warn('⚠️ Could not process formula includes:', e);
  }

  return productFormula;
};
